import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import MDUITextField from 'src/components/m3/textField';
import useStyles from './style';

function NormalInput({
  label,
  value,
  onChange,
  suffix = '',
  min = 0,
  max = Infinity,
  requirePositive = false
}) {
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const classes = useStyles();

  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(
        value !== null && value !== undefined ? String(value) : ''
      );
    }
  }, [value, isFocused]);

  const handleInputChange = e => {
    const inputValue = e.target.value;
    const cleanValue = suffix ? inputValue.replace(suffix, '') : inputValue;

    if (cleanValue === '' || /^\d*\.?\d*$/.test(cleanValue)) {
      if (suffix === '%' && cleanValue !== '') {
        const numValue = parseFloat(cleanValue);
        if (!Number.isNaN(numValue) && numValue > 100) {
          return;
        }
      }

      setDisplayValue(inputValue);

      if (cleanValue !== '') {
        const numValue = parseFloat(cleanValue);
        if (!Number.isNaN(numValue)) {
          const clampedValue = Math.max(min, Math.min(max, numValue));
          onChange(clampedValue);
        }
      } else {
        onChange(null);
      }
    }
  };

  const handleFocus = () => {
    setIsFocused(true);

    if (suffix && displayValue.includes(suffix)) {
      setDisplayValue(displayValue.replace(suffix, ''));
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    const cleanValue = displayValue.replace(suffix, '').trim();
    if (cleanValue === '') {
      onChange(null);
      setDisplayValue('');
    } else {
      const numValue = parseFloat(cleanValue);
      const clampedValue = Math.max(min, Math.min(max, numValue));
      setDisplayValue(String(clampedValue));
      onChange(clampedValue);
    }
  };

  const handleKeyPress = e => {
    const cleanValue = displayValue.replace(suffix, '').trim();

    if (
      !/[0-9]/.test(e.key) &&
      ![
        'Backspace',
        'Delete',
        'Tab',
        'Enter',
        'ArrowLeft',
        'ArrowRight'
      ].includes(e.key)
    ) {
      e.preventDefault();
      return;
    }
    if (suffix === '%') {
      if (/[0-9]/.test(e.key)) {
        if (cleanValue.length === 2) {
          if (!(cleanValue === '10' && e.key === '0')) {
            e.preventDefault();
          }
        }
        if (cleanValue.length >= 3) {
          e.preventDefault();
        }
      }
    }
  };

  const formattedDisplayValue =
    suffix && !isFocused && displayValue && displayValue !== ''
      ? `${displayValue}${suffix}`
      : displayValue;

  const isInvalid = requirePositive && value === 0;

  return (
    <div style={{ width: '100%' }}>
      <MDUITextField
        label={label}
        variant="outlined"
        value={formattedDisplayValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyPress}
        className={classes.textField}
        style={{
          width: '100%',
          fontFamily: 'Overused Grotesk'
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: isInvalid ? 'red' : undefined
            },
            '&:hover fieldset': {
              borderColor: isInvalid ? 'red' : undefined
            },
            '&.Mui-focused fieldset': {
              borderColor: isInvalid ? 'red' : undefined
            }
          },
          '& .MuiInputLabel-root': {
            color: isInvalid ? 'red' : undefined
          }
        }}
      />
      {isInvalid && (
        <Typography
          variant="caption"
          style={{
            color: 'red',
            marginTop: '4px',
            fontSize: '12px',
            display: 'block'
          }}
        >
          Value should be &gt; 0
        </Typography>
      )}
    </div>
  );
}

NormalInput.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  suffix: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  requirePositive: PropTypes.bool
};

NormalInput.defaultProps = {
  suffix: '',
  min: 0,
  max: Infinity,
  value: null,
  requirePositive: false
};

export default NormalInput;
